import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Card } from '@/ui/Card';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import type { Curso, EstadoDeAsignacion } from './api';

/**
 * piezas.tsx — lo que el catálogo y el detalle del curso comparten.
 *
 * ## Los cuatro tipos, con su icono
 *
 * En el vanilla cada tipo traía además dos colores propios escritos a mano
 * —uno para el fondo del chip y otro para el icono sobre la portada—. Aquí el
 * chip es el `Badge` del sistema: los cuatro tipos se distinguen por el icono y
 * el nombre, no por un color inventado que nadie más usa.
 */
export const TIPOS: Record<string, { etiqueta: string; icono: string }> = {
  induccion: { etiqueta: 'Inducción', icono: 'bi-mortarboard-fill' },
  curso: { etiqueta: 'Curso', icono: 'bi-book-fill' },
  webinar: { etiqueta: 'Webinar', icono: 'bi-camera-video-fill' },
  taller: { etiqueta: 'Taller', icono: 'bi-tools' },
};

export const NIVELES: Record<string, string> = {
  basico: 'Básico',
  intermedio: 'Intermedio',
  avanzado: 'Avanzado',
};

export const MODALIDADES: Record<string, string> = {
  autonomo: 'Autónomo',
  virtual: 'Virtual',
  presencial: 'Presencial',
  mixto: 'Mixto',
};

/**
 * La paleta de portadas.
 *
 * Es **dato**: se guarda en `cover_icon_color` de cada curso y quien lo crea la
 * elige. El nombre no es decoración — es lo que anuncia el lector de pantalla,
 * porque ocho botones de color sin nombre son ocho «botón» idénticos.
 */
export const COLORES_DE_PORTADA: ReadonlyArray<readonly [string, string]> = [
  ['#0D2B4E', 'Azul institucional'], ['#2980B9', 'Azul'],
  ['#8E44AD', 'Morado'], ['#27AE60', 'Verde'],
  ['#E74C3C', 'Rojo'], ['#F5A623', 'Naranja'],
  ['#16213e', 'Azul noche'], ['#1a1a2e', 'Grafito'],
];

/**
 * El icono guardado de una portada: un nombre de Bootstrap Icons o, en las filas
 * antiguas, un emoji.
 *
 * ## Por qué existe
 *
 * Antes del selector de iconos, el de un curso o una ruta se escribía a mano en
 * un campo de cuatro caracteres, y lo que se escribía era un emoji. Medido el
 * 2026-09-21: **los 7 cursos activos y la única ruta** lo siguen teniendo así
 * —📚, 🦺, 🧭…— y ningún curso tiene imagen de portada. El vanilla los pintaba
 * con `Utils.iconHtml`, que distingue las dos formas.
 *
 * Las opciones 1 y 2 metían el valor tal cual en un `className` de Bootstrap
 * Icons, y un emoji ahí no pinta nada: **todas las portadas salían como un
 * bloque de color vacío**. Ninguna prueba lo vio, y ninguna de las dos se había
 * abierto en el navegador. Se encontró midiendo la base al empezar la opción 3.
 *
 * El emoji va como texto —React lo escapa— y nunca dentro de un `className`.
 */
export function IconoDePortada({
  valor, respaldo, className,
}: {
  valor?: string | null;
  respaldo: string;
  className?: string;
}) {
  const v = (valor || '').trim();
  if (/^bi-[a-z0-9-]+$/.test(v)) return <i className={cn('bi', v, className)} aria-hidden="true" />;
  if (/^mdi-[a-z0-9-]+$/.test(v)) return <i className={cn('mdi', v, className)} aria-hidden="true" />;
  if (v) return <span className={className} aria-hidden="true">{v}</span>;
  return <i className={cn('bi', respaldo, className)} aria-hidden="true" />;
}

/** El semáforo de una asignación formal: qué tono y qué se dice. */
const VENCIMIENTO: Record<EstadoDeAsignacion, { tono: 'danger' | 'warn' | 'info'; texto: string }> = {
  vencido: { tono: 'danger', texto: 'Venció el' },
  proximo: { tono: 'warn', texto: 'Vence pronto:' },
  al_dia: { tono: 'info', texto: 'Vence el' },
};

/* ── La tarjeta del catálogo ───────────────────────────────────────────── */

/**
 * Un curso en la rejilla.
 *
 * Es un `<a>` con `href` de verdad —no el `<div onclick>` que fue— así que se
 * tabula, se activa con Enter, se abre en otra pestaña con Ctrl+clic y hereda
 * el anillo de foco. Eso ya se había arreglado en el vanilla y se conserva.
 *
 * Lo que cambia: las diez insignias que se pintaban con `rgba()` y `#hex`
 * escritos a mano pasan a `Badge`, así que siguen a la marca y al tema. Eran
 * seis tonos distintos en la misma tarjeta.
 */
export function TarjetaDeCurso({ c }: { c: Curso }) {
  const pct = Math.round(c.progress_pct || 0);
  const hecho = c.completed || pct >= 100;
  const tipo = TIPOS[c.course_type] ?? TIPOS.curso!;
  const borrador = c.status && c.status !== 'publicado';
  const vence = c.assigned && !hecho && c.assignment_due
    ? VENCIMIENTO[c.assignment_status ?? 'al_dia']
    : null;

  return (
    <Card interactiva className="relative flex w-full flex-col overflow-hidden">
      <div className="relative flex h-28 shrink-0 items-center justify-center overflow-hidden bg-sunken">
        {c.cover_url ? (
          // Decorativa: el título va como texto justo debajo, y un `alt` con el
          // mismo título haría que el lector lo leyera dos veces.
          <img src={c.cover_url} alt="" loading="lazy" className="size-full object-cover" />
        ) : (
          <div
            className="flex size-full items-center justify-center"
            style={{ background: c.cover_icon_color || '#0D2B4E' }}
            aria-hidden="true"
          >
            <IconoDePortada valor={c.cover_icon} respaldo="bi-mortarboard" className="text-8 leading-none text-white" />
          </div>
        )}

        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-xs bg-black/55 px-1.5 py-0.5 text-1 font-semibold text-white">
          <i className={cn('bi', tipo.icono)} aria-hidden="true" /> {tipo.etiqueta}
        </span>

        {borrador && (
          <span className="absolute bottom-2 left-2 rounded-xs bg-black/55 px-1.5 py-0.5 text-1 font-semibold text-white">
            {c.status === 'borrador' ? 'Borrador' : 'Archivado'}
          </span>
        )}

        {hecho ? (
          <span
            className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full bg-ok text-white"
            title="Completado"
          >
            <i className="bi bi-check-lg" aria-hidden="true" />
            <span className="sr-only">Completado</span>
          </span>
        ) : (c.is_mandatory || c.assignment_mandatory) ? (
          <span className="absolute right-2 top-2 rounded-xs bg-black/55 px-1.5 py-0.5 text-1 font-semibold text-white">
            Obligatorio
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <h3 className="text-3 font-bold leading-snug text-brand-ink [overflow-wrap:anywhere]">
          {/* `text-inherit`: la hoja va sin `preflight`, así que un enlace se
              queda con el azul del navegador en vez de heredar el del título. */}
          <a
            href={`#course-detail/${c.id}`}
            className="rounded-xs text-inherit no-underline after:absolute after:inset-0 focus-visible:focus-anillo"
            aria-label={`${c.title}${hecho ? ' — completado' : c.enrolled ? ` — ${pct}% completado` : ''}`}
          >
            {c.title}
          </a>
        </h3>

        {c.description && (
          <p className="line-clamp-2 text-2 text-muted [overflow-wrap:anywhere]">{c.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-2 text-ink-2">
          <span className="flex items-center gap-1.5">
            <i className="bi bi-collection-play" aria-hidden="true" />
            {c.lesson_count || 0} {(c.lesson_count || 0) === 1 ? 'lección' : 'lecciones'}
          </span>
          {c.level && <Badge tone="info">{NIVELES[c.level] ?? c.level}</Badge>}
          {hecho ? (
            <Badge tone="ok" className="ml-auto">
              <i className="bi bi-check-circle-fill" aria-hidden="true" /> Completado
            </Badge>
          ) : c.enrolled ? (
            <Badge tone="warn" className="ml-auto">
              <i className="bi bi-play-fill" aria-hidden="true" /> En progreso
            </Badge>
          ) : null}
        </div>

        {vence && (
          <p>
            <Badge tone={vence.tono}>
              <i className="bi bi-calendar-event" aria-hidden="true" /> {vence.texto}{' '}
              {Utils.fmtDateOnly(c.assignment_due!)}
            </Badge>
          </p>
        )}

        {c.enrolled ? (
          <div className="mt-auto flex items-center gap-2 pt-1">
            <div className="flex-1">
              <BarraDeProgreso pct={pct} etiqueta="Avance del curso" />
            </div>
            <span className={cn('text-1 font-bold tabular-nums', hecho ? 'text-ok-ink' : 'text-ink-2')}>
              {pct}%
            </span>
          </div>
        ) : (
          <p className="mt-auto flex items-center gap-1 pt-1 text-2 font-semibold text-brand-ink">
            Empezar <i className="bi bi-arrow-right-short" aria-hidden="true" />
          </p>
        )}
      </div>
    </Card>
  );
}

/* ── Las píldoras de filtro ────────────────────────────────────────────── */

/**
 * Un grupo de filtros se comporta como un conjunto de botones de estado:
 * **`aria-pressed` es lo que anuncia el lector de pantalla**, y la clase solo
 * pinta. En el vanilla solo existía la clase hasta que se corrigió; aquí no se
 * puede perder, porque el estado y el atributo salen del mismo dato.
 */
export function PildorasDeFiltro<T extends string>({
  etiqueta, opciones, activa, onElegir,
}: {
  etiqueta: string;
  opciones: ReadonlyArray<{ valor: T; texto: string; icono?: string; punto?: string; cuenta?: number }>;
  activa: T;
  onElegir: (v: T) => void;
}) {
  return (
    <div role="group" aria-label={etiqueta} className="mb-3 flex flex-wrap gap-2">
      {opciones.map((o) => {
        const puesta = o.valor === activa;
        return (
          <button
            key={o.valor}
            type="button"
            aria-pressed={puesta}
            onClick={() => onElegir(o.valor)}
            className={cn(
              'flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-2 font-semibold',
              'pointer-coarse:min-h-11 focus-visible:focus-anillo',
              puesta
                ? 'border-brand-ink bg-brand text-on-brand'
                : 'border-line bg-surface text-ink-2 hover:border-brand-ink hover:text-brand-ink',
            )}
          >
            {o.punto && (
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: o.punto }}
                aria-hidden="true"
              />
            )}
            {o.icono && <i className={cn('bi', o.icono)} aria-hidden="true" />}
            {o.texto}
            {o.cuenta != null && <span className="opacity-70">({o.cuenta})</span>}
          </button>
        );
      })}
    </div>
  );
}
